import React, {useContext, useEffect, useRef, useState} from 'react';
import {Toast} from "primereact/toast";
import {Button} from "primereact/button";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Dialog} from "primereact/dialog";
import {confirmPopup, ConfirmPopup} from "primereact/confirmpopup";
import {Dropdown} from "primereact/dropdown";
import {LayoutContext} from "../../../../../layout/context/LayoutContext";
import {usePagination} from "../../../../../layout/hooks/usePagination";
import callToast, {
    handleChangeData,
    handleDelete,
    mergeWithSelected,
    postData,
    validateFormFields
} from "../../../../../utils/helper";
import {useFetch} from "../../../../../layout/hooks/useFetch";
import {webInfo} from "../../../../../utils/webInfo";
import PageHeading from "../../../../../Components/PageHeading";
import Label from "../../../../../Components/Label";
import {InputNumber} from "primereact/inputnumber";


interface InitialState {

    user_id: { id?: number, name: string, email: string } | null,
    vehicle_id: { id: number, model: string } | null,
    vehicle_fuel_type_id: { id: number, name: string } | null,
    last_fuel_reading: number | null,
    fuel_limit: number | null,
}

const RefuelingLog = () => {
    const initialState: InitialState = {
        user_id: null,
        vehicle_id: null,
        vehicle_fuel_type_id: null,
        last_fuel_reading: null,
        fuel_limit: null,
    };
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const {accessToken} = useContext(LayoutContext)
    const [refresh, setRefresh] = useState<boolean>(false);
    const [formData, setFormData] = useState<InitialState>(initialState);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const toast = useRef<Toast | null>(null)
    const deleteId = useRef<number>(0)

    const [modal, setModal] = useState<boolean>(false);
    const [updatedId, setUpdatedId] = useState<number | null>(null);

    const [userFilter, setUserFilter] = useState<string>('');
    const [userOptions, setUserOptions] = useState<{ id: number, name: string, email: string }[]>([]);

    const [fuelFilter, setFuelFilter] = useState<string>('');
    const [fuelOptions, setFuelOptions] = useState<{ id: number, name: string }[]>([]);

    const [vehicleNameFilter, setVehicleNameFilter] = useState<string>('');
    const [vehicleOptions, setVehicleOptions] = useState<{ id: number, model: string }[]>([]);


    // pagination
    const {page, limitData, handlePageChange} = usePagination();

    // endpoint
    const mainTitle = "Re-fuel Log"
    const tableDataEndpointUrl = `/vehicle-refuel-logs?page=${page} `
    const submitFormEndpointUrl = updatedId ? `/vehicle-refuel-logs/${updatedId}` : "/vehicle-refuel-logs";

    

    const accept = () => {
        const deleteParams = {
            id: deleteId.current,
            startEndpoint: "/vehicle-refuel-logs/",
            // finalEndpoint: finalEndpoint,
            name: "Re-fuel Log",
            setUpdate: setRefresh,
            token: accessToken,
            toast: toast,
        }

        handleDelete(deleteParams).then()
    }


    const {data: userRawData} = useFetch(
        `/employees?page=1&per_page=10${userFilter ? `&name=${userFilter}` : ''}`,
        [userFilter]
    );

    useEffect(() => {
        if (userRawData) {
            const modifiedData = userRawData?.map((item: any) => ({
                id: item?.id,
                name: item?.name,
                email: item?.email,
            }));
            setUserOptions(mergeWithSelected(modifiedData, formData?.user_id));
        }
    }, [userRawData, formData?.user_id]);

    const {data: vehicleRawData} = useFetch(
        `/vehicle?page=1&per_page=10${vehicleNameFilter ? `&model=${vehicleNameFilter}` : ''}`,
        [vehicleNameFilter]
    );

    useEffect(() => {
        if (vehicleRawData) {
            const modifiedData = vehicleRawData?.map((item: any) => ({
                id: item?.id,
                model: item?.model,
                // serial_number: item?.serial_number,
            }));
            setVehicleOptions(mergeWithSelected(modifiedData, formData?.vehicle_id));
        }
    }, [vehicleRawData, formData?.vehicle_id]);

    const {data: fuelRawData} = useFetch(
        `/vehicle-fuel-types?page=1&per_page=10${fuelFilter ? `&name=${fuelFilter}` : ''}`,
        [fuelFilter]
    );

    useEffect(() => {
        if (fuelRawData) {
            const modifiedData = fuelRawData?.map((item: any) => ({
                id: item?.id,
                name: item?.name,
            }));
            setFuelOptions(mergeWithSelected(modifiedData, formData?.vehicle_fuel_type_id));
        }
    }, [fuelRawData, formData?.vehicle_fuel_type_id]);




    // fetch data
    const {
        data: tableDataList,
        totalData, loading
    } = useFetch(tableDataEndpointUrl, [refresh])


    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const validationRules = [
            {field: 'user_id', label: 'User'},
            {field: 'vehicle_id', label: 'Vehicle'},
            {field: 'fuel_type', label: 'Fuel Type'},
            {field: 'last_fuel_reading', label: 'Last Fuel Reading'},
            {field: 'fuel_limit', label: 'Fuel Limit'},
        ];

        if (!validateFormFields(formData, validationRules, toast)) return;

        const payload = {
            // name: formData.name.replace(/\s+/g, ' ').trim()
            user_id: formData.user_id?.id,
            vehicle_id: formData.vehicle_id?.id,
            vehicle_fuel_type_id: formData.vehicle_fuel_type_id?.id,
            last_fuel_reading: formData.last_fuel_reading,
            fuel_limit: formData.fuel_limit,

        };

        try {
            setIsSubmitting(true);
            const response = await fetch(`${webInfo?.baseUrl}${submitFormEndpointUrl}`, postData("PUT", accessToken, payload));

            if (response.ok) {
                callToast(toast, true, `${mainTitle} ${updatedId ? 'updated' : 'created'} successfully`);
                setFormData(initialState)
                setModal(false)
                setUpdatedId(null)
                setRefresh(!refresh);
                setFuelFilter('')
                setUserFilter('')
                setVehicleNameFilter('')
                return;
            } else {
                const resData = await response.json();
                callToast(toast, false, resData.error);
            }

        } catch (err) {
            console.error('Failed to update', err);
        } finally {
            setIsSubmitting(false);
        }

    }

    // row edit template
    const actionTemplate = (rowData: any) => {

        return (
            <div className={'flex align-items-center justify-content-end gap-2'}>
                <Button
                    icon={'pi pi-pencil'}
                    className={'border-rounded w-2rem h-2rem'}
                    onClick={() => {

                        setModal(true)
                        setUpdatedId(rowData?.id)
                        setFuelFilter('')
                        setUserFilter('')
                        setVehicleNameFilter('')
                        setFormData({
                            user_id: rowData?.user,
                            vehicle_id: rowData?.vehicle,
                            vehicle_fuel_type_id: rowData?.vehicle_fuel_type,
                            last_fuel_reading: rowData?.last_fuel_reading,
                            fuel_limit: rowData?.fuel_limit,
                        })
                    }}
                    outlined/>

                <Button
                    icon={'pi pi-trash'}
                    // tooltip={'Delete reservation'}
                    className={'border-rounded w-2rem h-2rem'}
                    outlined
                    onClick={(event: any) => {
                        // setDeletedFloorId(rowData?.id);
                        deleteId.current = rowData?.id;
                        confirmPopup({
                            target: event.currentTarget,
                            message: `Do you want to delete this re-fuel log?`,
                            // message: `Do you want to delete this ${mainTitle.toLowerCase()}`,
                            icon: 'pi pi-info-circle',
                            acceptClassName: 'p-button-danger',
                            accept,
                            onHide() {
                                deleteId.current = 0;
                            }
                        })
                    }}
                />
            </div>
        )
    }

    const vehicleInformationBodyTemplate = (rowData: any) => {
        return (
            <div className="flex flex-column">
                <span className="font-semibold text-sm text-900">{rowData?.vehicle?.model}</span>
                {/*<span className="text-xs text-700">{rowData?.vehicle_ownership?.registration_number}</span>*/}
            </div>
        );
    };

    const userInformationBodyTemplate = (rowData: any) => {
        return (
            <div className="flex flex-column">
                <span className="font-semibold text-sm text-900">{rowData?.user?.name}</span>
                <span className="text-xs text-700">{rowData?.user?.email}</span>
            </div>
        )
    }



    const formUserDropdownItemTemplate = (option: any) => {
        return (
            <div className="flex flex-column gap-1">
                <span className="font-semibold text-900 text-sm">{option?.name}</span>
                <span className="text-xs text-700">{option?.email}</span>
            </div>
        );
    }

    const formVehicleDropdownItemTemplate = (option: any) => {

        return (
            <div className="flex flex-column gap-1">
                <span className="font-semibold text-900 text-sm">{option?.model}</span>
                <span className="text-xs text-700">{option?.registration_number}</span>
            </div>
        )
    }



    return (
        <div className={"card"}>
            <Toast ref={toast}/>
            <ConfirmPopup/>
            <PageHeading title={mainTitle}
                         isCreate={true}
                         createFunction={() => setModal(true)}
                         back={true}
            />

            <DataTable
                value={tableDataList}
                dataKey="id"
                tableStyle={{minWidth: '50rem'}}
                paginator={tableDataList?.length > 0}
                lazy
                first={(page - 1) * limitData}
                rows={limitData}
                totalRecords={totalData}
                loading={loading}
                onPage={handlePageChange}
            >
                <Column
                    field="vehicle"
                    header="Vehicle Information"
                    body={vehicleInformationBodyTemplate}
                    style={{width: '20%'}}
                    headerClassName={"white-space-nowrap"}
                />

                <Column
                    field={"user"}
                    header="User Information"
                    style={{width: '20%'}}
                    body={userInformationBodyTemplate}
                    headerClassName={"white-space-nowrap "}
                />

                <Column
                    field={"vehicle_fuel_type.name"}
                    header="Fuel Type"
                    style={{width: '20%'}}
                    headerClassName={"white-space-nowrap "}
                />

                <Column
                    field={"last_fuel_reading"}
                    header="Last Fuel Reading (L)"
                    style={{width: '20%'}}
                    headerClassName={"white-space-nowrap "}
                />

                <Column
                    field={"fuel_limit"}
                    header="Fuel Limit (L)"
                    style={{width: '20%'}}
                    headerClassName={"white-space-nowrap "}
                />

                <Column header={"Action"} rowEditor={true}
                        bodyStyle={{textAlign: 'center'}}
                        body={actionTemplate}
                        headerClassName={"flex justify-content-end"}
                />
            </DataTable>

            <Dialog visible={modal}
                    style={{width: "50rem"}}
                    headerClassName="text-800 font-semibold text-xl"
                    header={updatedId ? `Edit ${mainTitle}` : `New ${mainTitle}`}
                    breakpoints={{"960px": "75vw", "640px": "90vw"}}
                    onHide={() => {
                        setUpdatedId(null)
                        setModal(false)
                        setFormData(initialState)
                        setFuelFilter('')
                        setUserFilter('')
                        setVehicleNameFilter('')
                    }}
            >
                <div>
                    <form className={''}
                          onSubmit={handleSubmit}
                    >
                        <div className="grid ">
                            <div className="col-12  md:col-5">
                                <Label label={"Vehicle"} required={!updatedId}/>
                                <Dropdown
                                    options={vehicleOptions? vehicleOptions : []}
                                    optionLabel="model"
                                    id="vehicle_id"
                                    name={`vehicle_id`}
                                    className={`w-full`}
                                    value={formData?.vehicle_id}
                                    onChange={(e) => handleChangeData('vehicle_id', e.value, formData, setFormData)}
                                    placeholder="Select vehicle"
                                    filter
                                    onFilter={(e) => {
                                        if (timeoutRef?.current) {
                                            clearTimeout(timeoutRef.current);
                                        }
                                        timeoutRef.current = setTimeout(() => {
                                            setVehicleNameFilter(e.filter)
                                        }, 500);

                                        if (vehicleRawData) {
                                            const modifiedData = vehicleRawData.map((item: any) => ({
                                                id: item?.id,
                                                model: item?.model,
                                                serial_number: item?.serial_number,
                                            }));
                                            setVehicleOptions(modifiedData);
                                        }
                                    }}
                                    // valueTemplate={(option)=>option? option?.model + '-' + option?.serial_number : 'Select vehicle'}
                                    itemTemplate={formVehicleDropdownItemTemplate}
                                    showClear
                                    showFilterClear
                                />
                            </div>
                            <div className="col-12  md:col-5">
                                <Label label={"User"} required={!updatedId}/>
                                <Dropdown
                                    options={userOptions? userOptions : []}
                                    optionLabel="name"
                                    id="user_id"
                                    name={`user_id`}
                                    className={`w-full`}
                                    value={formData?.user_id}
                                    onChange={(e) => handleChangeData('user_id', e.value, formData, setFormData)}
                                    placeholder="Select user"
                                    filter
                                    onFilter={(e) => {
                                        if (timeoutRef?.current) {
                                            clearTimeout(timeoutRef.current);
                                        }
                                        timeoutRef.current = setTimeout(() => {
                                            setUserFilter(e.filter)
                                        }, 500);

                                        if (userRawData) {
                                            const modifiedData = userRawData.map((item: any) => ({
                                                id: item?.id,
                                                name: item?.name,
                                                email: item?.email,
                                            }));
                                            setUserOptions(modifiedData);
                                        }
                                    }}
                                    // valueTemplate={(option)=>option? option?.name + '-' + option?.email : 'Select user'}
                                    itemTemplate={formUserDropdownItemTemplate}
                                    showClear
                                    showFilterClear
                                />
                            </div>
                        </div>
                        <div className="grid md:mt-2">
                            <div className="col-12  md:col-4">
                                <Label label={"Fuel"} required={!updatedId}/>
                                <Dropdown
                                    options={fuelOptions? fuelOptions : []}
                                    optionLabel="name"
                                    id="vehicle_fuel_type_id"
                                    name={`vehicle_fuel_type_id`}
                                    className={`w-full`}
                                    value={formData?.vehicle_fuel_type_id}
                                    onChange={(e) => handleChangeData('vehicle_fuel_type_id', e.value, formData, setFormData)}
                                    placeholder="Select fuel"
                                    filter
                                    onFilter={(e) => {
                                        if (timeoutRef?.current) {
                                            clearTimeout(timeoutRef.current);
                                        }
                                        timeoutRef.current = setTimeout(() => {
                                            setFuelFilter(e.filter)
                                        }, 500);

                                        if (fuelRawData) {
                                            const modifiedData = userRawData.map((item: any) => ({
                                                id: item?.id,
                                                name: item?.name,
                                            }));
                                            setFuelOptions(modifiedData);
                                        }
                                    }}
                                    // valueTemplate={(option)=>option? option?.name + '-' + option?.email : 'Select user'}
                                    itemTemplate={formUserDropdownItemTemplate}
                                    showClear
                                    showFilterClear
                                />
                            </div>

                            <div className="col-12  md:col-4">
                                <Label label={"Last Fuel Reading"} required={!updatedId}/>
                                <InputNumber
                                    id="last_fuel_reading"
                                    value={formData.last_fuel_reading}
                                    name={"last_fuel_reading"}
                                    mode="decimal"
                                    minFractionDigits={1}
                                    maxFractionDigits={2}
                                    // maxFractionDigits={2}
                                    suffix={" L"}
                                    onValueChange={(e) => handleChangeData('last_fuel_reading', e.value, formData, setFormData)}
                                    placeholder={"Enter last fuel reading"}
                                    className={`w-full p-fluid`}
                                />
                            </div>
                            <div className="col-12  md:col-4">
                                <Label label={"Fuel Limit"} required={!updatedId}/>
                                <InputNumber
                                    id="fuel_limit"
                                    value={formData.fuel_limit}
                                    name={"fuel_limit"}
                                    mode="decimal"
                                    minFractionDigits={1}
                                    maxFractionDigits={2}
                                    // maxFractionDigits={2}
                                    suffix={" L"}
                                    onValueChange={(e) => handleChangeData('fuel_limit', e.value, formData, setFormData)}
                                    placeholder={"Enter fuel limit"}
                                    className={`w-full p-fluid`}
                                />
                            </div>
                        </div>
                        <div className="flex gap-2 mt-2">
                            <Button
                                type="submit"
                                label={updatedId ? 'Update' : "Create"}
                                className="p-button-primary"
                                loading={isSubmitting}
                                size={'small'}
                            />
                        </div>
                    </form>
                </div>
            </Dialog>

        </div>
    );
};

export default RefuelingLog;